import { Injectable } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { XmlHttpRequestHelper } from '@shared/helpers/XmlHttpRequestHelper';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { UserScopedState } from '@shared/common/session/user-scoped-state';

@Injectable()
export class AppAuthService {

    logout(reload?: boolean, returnUrl?: string, preserveMicrosoftSso?: boolean): void {
        let customHeaders = {
            [abp.multiTenancy.tenantIdCookieName]: abp.multiTenancy.getTenantIdCookie(),
            'Authorization': 'Bearer ' + abp.auth.getToken()
        };

        XmlHttpRequestHelper.ajax(
            'GET',
            AppConsts.remoteServiceBaseUrl + '/api/TokenAuth/LogOut',
            customHeaders,
            null,
            () => {
                abp.auth.clearToken();
                abp.auth.clearRefreshToken();
                if (preserveMicrosoftSso) {
                    AppAuthService.markMicrosoftSsoResume();
                } else {
                    this.clearMicrosoftAuthCache();
                    AppAuthService.clearMicrosoftSsoState();
                }
                new LocalStorageService().removeItem(AppConsts.authorization.encrptedAuthTokenName);
                UserScopedState.clearOnLogout();
                if (reload !== false) {
                    if (returnUrl) {
                        location.href = returnUrl;
                    } else {
                        location.href = '';
                    }
                }
            }
        );
    }

    static markMicrosoftSsoResume(loginHint?: string): void {
        try {
            localStorage.setItem(AppConsts.microsoftSso.resumeAfterTimeoutKey, 'true');
            if (loginHint) {
                localStorage.setItem(AppConsts.microsoftSso.loginHintKey, loginHint);
            }
        } catch {
            // Ignore quota / private-mode failures; interactive Microsoft login still works.
        }
    }

    static persistMicrosoftSsoLoginHint(loginHint?: string): void {
        try {
            if (loginHint) {
                localStorage.setItem(AppConsts.microsoftSso.loginHintKey, loginHint);
            }
            localStorage.removeItem(AppConsts.microsoftSso.resumeAfterTimeoutKey);
        } catch {
            // Ignore quota / private-mode failures.
        }
    }

    static clearMicrosoftSsoState(): void {
        try {
            localStorage.removeItem(AppConsts.microsoftSso.resumeAfterTimeoutKey);
            localStorage.removeItem(AppConsts.microsoftSso.loginHintKey);
        } catch {
            // Ignore storage failures.
        }
    }

    static shouldResumeMicrosoftSso(): boolean {
        try {
            return localStorage.getItem(AppConsts.microsoftSso.resumeAfterTimeoutKey) === 'true';
        } catch {
            return false;
        }
    }

    static getMicrosoftSsoLoginHint(): string | null {
        try {
            return localStorage.getItem(AppConsts.microsoftSso.loginHintKey);
        } catch {
            return null;
        }
    }

    private clearMicrosoftAuthCache(): void {
        this.removeMicrosoftAuthItems(sessionStorage);
        this.removeMicrosoftAuthItems(localStorage);
    }

    private removeMicrosoftAuthItems(storage: Storage): void {
        Object.keys(storage)
            .filter(key => key.indexOf('msal') >= 0)
            .forEach(key => storage.removeItem(key));
    }
}
